import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Package,
  Search,
  FileText,
  CheckCircle2,
  X,
  ArrowRight,
  Building2,
  Bell,
} from 'lucide-react';

const STORAGE_KEY = 'surplify_onboarding_done';

const STEP_ICONS = [
  <Package className="h-14 w-14 text-orange-500" />,
  <Building2 className="h-14 w-14 text-blue-500" />,
  <Search className="h-14 w-14 text-emerald-500" />,
  <FileText className="h-14 w-14 text-violet-500" />,
];

const STEP_ACCENT_COLORS = [
  'bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-800/50',
  'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800/50',
  'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800/50',
  'bg-violet-50 dark:bg-violet-950/30 border-violet-200 dark:border-violet-800/50',
];

const STEP_PROGRESS_COLORS = ['bg-orange-500', 'bg-blue-500', 'bg-emerald-500', 'bg-violet-500'];

export function OnboardingWizard({ onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  const TOTAL_STEPS = 4;

  const steps = [
    {
      title: t('onboarding.step1Title'),
      desc: t('onboarding.step1Desc'),
      points: t('onboarding.step1Points', { returnObjects: true }),
    },
    {
      title: t('onboarding.step2Title'),
      desc: t('onboarding.step2Desc'),
      points: t('onboarding.step2Points', { returnObjects: true }),
    },
    {
      title: t('onboarding.step3Title'),
      desc: t('onboarding.step3Desc'),
      points: t('onboarding.step3Points', { returnObjects: true }),
    },
    {
      title: t('onboarding.step4Title'),
      desc: t('onboarding.step4Desc'),
      points: t('onboarding.step4Points', { returnObjects: true }),
    },
  ];

  const handleSkip = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    onClose();
  };

  const handleNext = () => {
    if (step < TOTAL_STEPS - 1) {
      setStep((s) => s + 1);
    } else {
      handleFinish();
    }
  };

  const handleFinish = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    onClose();
    navigate('/search');
  };

  const progress = ((step + 1) / TOTAL_STEPS) * 100;
  const current = steps[step];

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
      data-testid="onboarding-overlay"
    >
      <div
        className="relative w-full max-w-lg bg-background rounded-3xl shadow-2xl overflow-hidden"
        data-testid="onboarding-wizard"
      >
        {/* Header bar with progress */}
        <div className="px-6 pt-6 pb-4 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {t('onboarding.step')} {step + 1} {t('onboarding.of')} {TOTAL_STEPS}
            </span>
            <button
              onClick={handleSkip}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="onboarding-close-btn"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Step dots + progress bar */}
          <div className="flex items-center gap-2 mb-3">
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => i <= step && setStep(i)}
                className={`transition-all rounded-full ${
                  i === step
                    ? `h-2.5 w-8 ${STEP_PROGRESS_COLORS[step]}`
                    : i < step
                    ? 'h-2.5 w-2.5 bg-slate-400'
                    : 'h-2.5 w-2.5 bg-slate-200 dark:bg-slate-700'
                }`}
                data-testid={`onboarding-step-dot-${i}`}
                aria-label={`Step ${i + 1}`}
              />
            ))}
          </div>
          <Progress value={progress} className="h-1" data-testid="onboarding-progress-bar" />
        </div>

        {/* Step content */}
        <div className="p-8">
          {/* Icon */}
          <div
            className={`inline-flex p-5 rounded-2xl border-2 mb-6 ${STEP_ACCENT_COLORS[step]}`}
            data-testid={`onboarding-step-icon-${step}`}
          >
            {STEP_ICONS[step]}
          </div>

          {/* Title */}
          <h2
            className="font-manrope text-2xl font-extrabold mb-3 leading-tight"
            data-testid="onboarding-step-title"
          >
            {current.title}
          </h2>

          {/* Description */}
          <p className="text-muted-foreground text-sm leading-relaxed mb-6" data-testid="onboarding-step-desc">
            {current.desc}
          </p>

          {/* Bullet points */}
          <ul className="space-y-2.5" data-testid="onboarding-step-points">
            {Array.isArray(current.points) &&
              current.points.map((pt, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span className="text-foreground/80">{pt}</span>
                </li>
              ))}
          </ul>
        </div>

        {/* Footer actions */}
        <div className="px-8 pb-8 flex items-center justify-between gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSkip}
            className="text-muted-foreground hover:text-foreground text-sm"
            data-testid="onboarding-skip-btn"
          >
            {t('onboarding.skip')}
          </Button>

          <Button
            size="lg"
            className={`font-semibold gap-2 px-7 rounded-xl transition-all hover:scale-105 ${
              step === TOTAL_STEPS - 1
                ? 'bg-emerald-500 hover:bg-emerald-600'
                : 'bg-orange-500 hover:bg-orange-600'
            }`}
            onClick={handleNext}
            data-testid="onboarding-next-btn"
          >
            {step === TOTAL_STEPS - 1 ? t('onboarding.finish') : t('onboarding.next')}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook to decide whether to show the onboarding wizard.
 * Returns [showWizard, dismissWizard]
 */
export function useOnboarding(user) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!user) return;
    const done = localStorage.getItem(STORAGE_KEY);
    if (!done) {
      // Small delay so the page renders first
      const timer = setTimeout(() => setShow(true), 800);
      return () => clearTimeout(timer);
    }
  }, [user]);

  useEffect(() => {
    const handler = () => setShow(true);
    window.addEventListener('surplify:start-tour', handler);
    return () => window.removeEventListener('surplify:start-tour', handler);
  }, []);

  const dismiss = () => setShow(false);
  return [show, dismiss];
}
